import React from 'react'

import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { SingleValue } from 'react-select'

import { ArcadeFieldLabel, ArcadeInputDropdown } from '@cash-design-system/react'

import { FieldController } from '../FieldController/FieldController'

type DropdownOption = {
  value: string
  inputLabel?: string
}

export type FormInputDropdownProps<T extends DropdownOption> = {
  name: string
  label?: string
  autoFocus?: boolean
  options: T[]
  placeholder?: string
  ariaLabel?: string
  helpText?: string
  rules?: React.ComponentProps<typeof Controller>['rules']
  handleChange?: (option: T | null, action: string, onChange?: (value: SingleValue<T>) => void) => void
}

export const FormInputDropdown = <
  T extends {
    value: string
    inputLabel?: string
  },
>(
  props: FormInputDropdownProps<T>
) => {
  const { t } = useTranslation()
  const { name, label = '', options, handleChange, placeholder, ariaLabel, helpText, rules } = props

  return (
    <ArcadeFieldLabel label={label}>
      <FieldController
        FieldComponent={ArcadeInputDropdown<T>}
        name={name}
        rules={rules}
        componentProps={{
          items: options,
          getItemKey: (item: unknown) => (item as T).value,
          itemToString: (item: unknown) => (item as T).inputLabel ?? (item as T).value,
          placeholder: placeholder ?? t('Select...', { context: 'Placeholder text for dropdown' }),
          helpText,
          'aria-label': ariaLabel ?? label,
          onChange: (value: string | number) => {
            const selectedOption = options.find(opt => opt.value === value)
            if (selectedOption) {
              handleChange?.(selectedOption, 'select-option', undefined)
            }
          },
        }}
        allowUndefinedValue={true}
      />
    </ArcadeFieldLabel>
  )
}
